<!-- This is the old OTJ progress bar chart, which will be deprecated in favour of the new OTJ progress chart after testing -->
<template>
  <div class="otj-progress-bar-chart">
    <template v-if="ready">
      <k-chart-bar-line v-if="ready"
                        :seriesBar="barChartSeries"
                        :seriesBarTitle="prettyStatuses"
                        :seriesLineTitle="'Planned hours'"
                        :seriesLine="lineChartSeries"
                        :xaxisData="dateLabels"
                        :customBarColors="colours"
                        customLineColor="#c800fa"
                        :customTooltip="formatCustomTooltip"
                        yaxisTitle="Time spent (Hours)"
                        xaxisTitle="Month"
      ></k-chart-bar-line>
    </template>
    <k-loader v-else class="k-loader" />
  </div>
</template>

<style>
.k-loader {
  display: flex;
  justify-content: center;
}

.custom-tooltip-content {
  background-color: var(--kate-background-body);
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
}

.tooltip-block-title {
  background: var(--kate-background);
  font-size: 12px;
  font-weight: bold;
  padding: 8px 12px;
  margin: -8px -12px 4px;
  border-radius: 4px 4px 0 0;
}

.tooltip-section {
  margin-bottom: 8px;
}

.tooltip-section.last {
  margin-bottom: 20px;
}

.tooltip-section-title {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
}

.tooltip-series-item {
  display: flex;
  align-items: center;
  margin: 2px 0;
  font-size: 12px;
}

.tooltip-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid var(--kate-type-dark);
}

.tooltip-series-name {
  margin-right: 6px;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import useGlobalStore from '@stores/global';
import KChartBarLine from '@base-components/charts/k-chart-bar-line.vue';
import KLoader from '@base-components/k-loader.vue';

const CHART_VISIBLE_STATUSES = ['approved', 'pending', 'draft']; // Rejected excluded from chart
const COLOUR_MAP = {
  approved: 'var(--kate-success)',
  rejected: 'var(--kate-danger)',
  pending: 'var(--kate-primary)',
  draft: 'var(--kate-button-outlined-hover)',
};

export default {
  components: { KChartBarLine, KLoader },
  mixins: [TimeMixin],

  beforeMount() {
    this.getOtjChartData();
  },

  props: {
    userId: {
      type: Number,
    },
    programmeId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      store: useGlobalStore(),
      otjProgress: [],
      ready: false,
    };
  },
  computed: {
    isApp() {
      return this.store.isApp;
    },
    recordedMonthlyOtj() {
      return this.otjProgress?.recorded_monthly_otj || [];
    },
    approvedMonthlyOtj() {
      return this.recordedMonthlyOtj.filter(e => e.status === 'approved');
    },
    pendingMonthlyOtj() {
      return this.recordedMonthlyOtj.filter(e => e.status === 'pending');
    },
    draftMonthlyOtj() {
      return this.recordedMonthlyOtj.filter(e => e.status === 'draft');
    },
    expectedMonthlyOtj() {
      return this.otjProgress?.expected_monthly_otj || [];
    },
    dateLabels() {
      return this.expectedMonthlyOtj.map(e => e.pretty_period);
    },
    // Current month in YYYY-MM, UTC-based, for comparison
    currentPeriod() {
      const now = new Date();
      return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    },
    // For months in the future, recorded OTJ is set to 0
    visibleEndIndex() {
      const currentPeriodIndex = this.expectedMonthlyOtj.findIndex(p => p.period === this.currentPeriod);
      return currentPeriodIndex === -1 ? this.expectedMonthlyOtj.length : currentPeriodIndex;
    },
    barChartSeries() {
      // Include on visible statuses and set to 0 for future months
      const visibleSeries = this.recordedMonthlyOtj.filter(e => CHART_VISIBLE_STATUSES.includes(e.status));
      // Format the series as an array of arrays, one for each status, using only the cumulative value and set to 0 for future months
      const seriesData = CHART_VISIBLE_STATUSES.map(status => visibleSeries.filter(e => e.status === status).map((e, idx) => (idx > this.visibleEndIndex ? 0 : e.cumulative_otj_minutes / 60)));
      return seriesData;
    },
    lineChartSeries() {
      return this.expectedMonthlyOtj.map(e => e.cumulative_otj_minutes / 60);
    },
    prettyStatuses() {
      return CHART_VISIBLE_STATUSES.map(status => `${status.charAt(0).toUpperCase() + status.slice(1)} hours`);
    },
    colours() {
      return CHART_VISIBLE_STATUSES.map(status => COLOUR_MAP[status] || 'var(--kate-type-light)');
    },
    otjChartDataEndpoint() {
      if (this.isApp) {
        return `/api/curriculum/programmes/${this.programmeId}/monthly-otj-chart-data`;
      }
      return `/api/curriculum/admin/programmes/${this.programmeId}/monthly-otj-chart-data/${this.userId}`;
    },
  },
  methods: {
    getMonthlyDataAtIndex(dataPointIndex) {
      if (!this.recordedMonthlyOtj.length) return null;
      const zeroedData = { cumulative_otj_minutes: 0, monthly_otj_minutes: 0 };
      return {
        approved: this.visibleEndIndex >= dataPointIndex ? this.approvedMonthlyOtj[dataPointIndex] : zeroedData,
        pending: this.visibleEndIndex >= dataPointIndex ? this.pendingMonthlyOtj[dataPointIndex] : zeroedData,
        draft: this.visibleEndIndex >= dataPointIndex ? this.draftMonthlyOtj[dataPointIndex] : zeroedData,
      };
    },

    formatCustomTooltip({ dataPointIndex }) {
      if (!this.recordedMonthlyOtj.length) return '';

      // For future months, only show the expected hours in the tooltip
      if (dataPointIndex > this.visibleEndIndex) {
        return `<div class="custom-tooltip-content">
          <div class="tooltip-block-title">${this.dateLabels[dataPointIndex] || ''} - Expected OTJ</div>
          <div class="tooltip-section">
            <div class="tooltip-section-title">This Month:</div>
            ${this.formatTimeSpent(this.expectedMonthlyOtj[dataPointIndex]?.monthly_otj_minutes || 0)}
          </div>
          <div class="tooltip-section">
            <div class="tooltip-section-title">Total:</div>
            ${this.formatTimeSpent(this.expectedMonthlyOtj[dataPointIndex]?.cumulative_otj_minutes || 0)}
          </div>
        </div>`;
      }

      // Data for that month
      const monthlyRecordedData = this.getMonthlyDataAtIndex(dataPointIndex);
      // Recorded OTJ for each status that month
      const statusItems = CHART_VISIBLE_STATUSES.map((status, i) => {
        const minutes = monthlyRecordedData[status]?.monthly_otj_minutes || 0;
        return `<div class="tooltip-series-item">
          <span class="tooltip-marker" style="background-color: ${COLOUR_MAP[status]};"></span>
          <span class="tooltip-series-name">${this.prettyStatuses[i]}:</span>
          <span class="tooltip-value">${this.formatTimeSpent(minutes)}</span>
        </div>`;
      }).join('');

      // Approved cumulative and monthly hours vs expected
      const approvedCumulative = monthlyRecordedData.approved?.cumulative_otj_minutes || 0;
      const approvedMonthly = monthlyRecordedData.approved?.monthly_otj_minutes || 0;
      const expectedCumulative = this.expectedMonthlyOtj[dataPointIndex]?.cumulative_otj_minutes || 0;
      const expectedMonthly = this.expectedMonthlyOtj[dataPointIndex]?.monthly_otj_minutes || 0;
      return `<div class="custom-tooltip-content">
        <div class="tooltip-block-title">${this.dateLabels[dataPointIndex] || ''}</div>
        <div class="tooltip-section">${statusItems}</div>
        <div class="tooltip-section last">
          <div class="tooltip-section-title">Approved vs Expected:</div>
          ${this.formatTimeSpent(approvedMonthly)} / ${this.formatTimeSpent(expectedMonthly)}
        </div>
        <div class="tooltip-block-title">Total to Date</div>
        <div class="tooltip-section">
          <div class="tooltip-section-title">Approved vs Expected:</div>
          ${this.formatTimeSpent(approvedCumulative)} / ${this.formatTimeSpent(expectedCumulative)}
        </div>
      </div>`;
    },
    getOtjChartData() {
      this.ready = false;
      this.$logger.info('Getting monthly OTJ progress');
      this.$http.get(this.otjChartDataEndpoint).then(resp => {
        this.otjProgress = resp.data;
        this.$logger.info('Got monthly OTJ progress');
      }).catch(err => {
        this.$logger.autowarn('Could not get monthly OTJ progress', { programmeId: this.programmeId }, err);
        if (this.$http.errIn(err, [404])) {
          this.$ktoast.info('No OTJ data found for this programme');
        } else {
          this.$ktoast.showError('Could not get monthly OTJ progress', !this.isApp);
        }
      }).finally(() => {
        this.ready = true;
      });
    },
  },
};
</script>
